import { rgba } from 'polished';
import React, { useRef, useState } from 'react';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { tokens } from '../theme/tokens';

const Container = styled(Box)`
	position: relative;
`;

const Bar = styled(Box)`
	height: 8px;
	border-radius: 4px;
	background: ${(props) => props.theme.colors.lightBlueDark};
	overflow: hidden;
`;

const Fill = styled(Box)<{
	dragging?: boolean;
	value: number;
	fillMode?: 'forwards' | 'bidirectional';
}>`
	height: 8px;
	background: '#18D9D9';
	transition: 150ms background-color ease-out;
	transform-origin: left center;

	${(props) => props.dragging && `transition: .4s cubic-bezier(0, 1, 0, 1) left;`}
	${(props) => props.value < 0.5 && props.fillMode !== 'forwards' && `transform: scaleX(-1.0);`}

	${(props) => props.value <= 1.0 && `background-color: #76e303;`}
	${(props) => props.value < 0.75 && `background-color: #18d9d9;`}
	${(props) => props.value < 0.5 && `background-color: #f86d09;`}
	${(props) => props.value < 0.25 && `background-color: #e92e36;`}
`;

const Knob = styled(Box)<{ dragging?: boolean }>`
	position: absolute;
	top: -12px;
	width: 32px;
	height: 32px;
	border-radius: 16px;
	cursor: grab;
	background: white;
	box-shadow:
		0px 1px 2px ${tokens.text.secondary},
		0px 8px 16px ${rgba(tokens.text.secondary, 0.1)};

	${(props) => props.dragging && `transition: .4s cubic-bezier(0, 1, 0, 1) left;`}
`;

export function Slider(props: {
	value: number;
	onChange: (value: number) => void;
	fillMode?: 'none' | 'bidirectional' | 'forwards';
}) {
	const ref = useRef<HTMLDivElement>(null);

	const [delta, setDelta] = useState<number | undefined>(undefined);

	function onMouseDown(event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) {
		const startX = 'clientX' in event ? event.clientX : event.touches[0].clientX;
		const value = props.value;

		function clip(x: number) {
			return Math.max(0, Math.min(1, value + (x - startX) / ref.current!.clientWidth));
		}

		function onMouseMove(event: MouseEvent | TouchEvent) {
			const newValue = clip('clientX' in event ? event.clientX : event.touches[0].clientX);
			setDelta(newValue);
			props.onChange(newValue);

			event.preventDefault();
			event.stopPropagation();

			return false;
		}

		function onMouseUp() {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);

			window.removeEventListener('touchmove', onMouseMove);
			window.removeEventListener('touchend', onMouseUp);

			setDelta(undefined);
		}

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);

		window.addEventListener('touchmove', onMouseMove);
		window.addEventListener('touchend', onMouseUp);
	}

	const value = Math.max(0, Math.min(1, delta ?? props.value));

	return (
		<Container ref={ref}>
			<Bar>
				{props.fillMode !== 'none' && (
					<Fill
						dragging={delta === undefined}
						value={value}
						fillMode={props.fillMode}
						{...(props.fillMode === 'forwards'
							? {
									width: Math.abs(value * 100).toFixed() + '%',
								}
							: {
									marginLeft: '50%',
									width: Math.abs(value * 100 - 50).toFixed() + '%',
								})}
					/>
				)}
			</Bar>
			<Knob
				dragging={delta === undefined}
				style={{
					left: `calc(${(value * 100).toFixed()}% - 16px)`,
				}}
				onMouseDown={onMouseDown}
				onTouchStart={onMouseDown}
			/>
		</Container>
	);
}
